import React, { useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const todayET = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Days shown for a month view: the whole month + the first 7 days of the next (the sheet's "Day 1–7" tabs, but dated). */
export const stripDays = (monthKey) => {
  const [y, m] = monthKey.split('-').map(Number);
  const out = [];
  const last = new Date(y, m, 0).getDate();
  for (let d = 1; d <= last; d++) out.push(ymd(new Date(y, m - 1, d)));
  for (let d = 1; d <= 7; d++) out.push(ymd(new Date(y, m, d)));
  return out;
};

export const DayStrip = ({ monthKey, onMonth, date, onDate, info = {} }) => {
  const days = useMemo(() => stripDays(monthKey), [monthKey]);
  const today = todayET();
  const [y, m] = monthKey.split('-').map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const shift = (n) => { const d = new Date(y, m - 1 + n, 1); onMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  let lastMonth = null;
  const scroller = useRef(null);
  useEffect(() => {
    const el = scroller.current?.querySelector(`[data-testid="dailies-day-${date}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [date, monthKey]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="dailies-strip">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => shift(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="dailies-prev-month" title="Previous month"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-sm font-bold text-slate-900" data-testid="dailies-month-label">{label} <span className="text-slate-400 font-normal">+ first 7 days of next month (the sheet's Day 1–7) · scroll sideways for the rest</span></div>
        <div className="flex items-center gap-1">
          <button onClick={() => { const t = today.slice(0, 7); onMonth(t); onDate(today); }} className="text-xs font-semibold px-2 py-1 rounded-lg border border-slate-300 hover:bg-slate-50" data-testid="dailies-today">Today</button>
          <button onClick={() => shift(1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid="dailies-next-month" title="Next month"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="relative">
      <div ref={scroller} className="flex gap-1 overflow-x-auto pb-1 pr-8" style={{ scrollbarWidth: 'thin' }}>
        {days.map(d => {
          const dt = new Date(`${d}T00:00:00`);
          const mon = dt.toLocaleDateString('en-US', { month: 'short' });
          const showMonth = mon !== lastMonth; lastMonth = mon;
          const i = info[d] || {};
          const sel = d === date; const isToday = d === today; const past = d < today; const wk = dt.getDay() === 0 || dt.getDay() === 6;
          return (
            <button key={d} onClick={() => onDate(d)} data-testid={`dailies-day-${d}`}
              title={`${dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${i.jobs ? ` · ${i.jobs} jobs · ${i.people} people named` : ''}${i.projected ? ` · ${i.projected} projected, no names yet` : ''}`}
              className={`flex-shrink-0 w-[46px] rounded-lg border px-1 py-1.5 text-center leading-tight transition-colors ${sel ? 'bg-blue-600 border-blue-500 text-white' : isToday ? 'bg-sky-50 border-sky-400 text-slate-900' : past ? 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100' : wk ? 'bg-slate-50/60 border-slate-200 text-slate-600 hover:bg-white' : 'bg-white border-slate-300 text-slate-900 hover:bg-sky-50'}`}>
              <div className={`text-[9px] font-bold uppercase ${sel ? 'text-blue-100' : showMonth ? 'text-amber-700' : 'text-slate-400'}`}>{showMonth ? mon : dt.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}</div>
              <div className="text-sm font-bold">{dt.getDate()}</div>
              <div className={`text-[9px] font-semibold ${sel ? 'text-blue-100' : 'text-slate-500'}`}>
                {i.people ? <>{i.people}{i.projected ? <span className={sel ? '' : 'text-amber-700'}>+{i.projected}?</span> : null}</> : i.projected ? <span className={sel ? '' : 'text-amber-700'}>{i.projected}?</span> : '·'}
              </div>
              {(i.ops_jobs > 0 || i.projections > 0) && <div className={`mx-auto mt-0.5 h-1 w-4 rounded-full ${i.ops_jobs > 0 ? 'bg-emerald-500' : 'bg-amber-400'}`} />}
            </button>
          );
        })}
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
      </div>
    </div>
  );
};
